const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places';

function waitFor(check: () => boolean): Promise<void> {
  if (check()) return Promise.resolve();
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (check()) {
        clearInterval(interval);
        resolve();
      }
    }, 50);
  });
}

export function loadGoogleMapsScript(apiKey: string): Promise<void> {
  // Already loaded
  if (window.google?.maps?.places) return Promise.resolve();

  // Script tag exists but not yet loaded (loading in progress)
  if (document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
    return waitFor(() => !!window.google?.maps?.places);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
}

/**
 * Load Google Maps for the ScreenMap component.
 * Ensures the Map constructor is available (not just the bootstrap stub).
 */
export function loadGoogleMapsWithMarker(apiKey: string): Promise<void> {
  if (window.google?.maps?.Map) return Promise.resolve();

  // If script is already loading/loaded, just wait for Map to be ready
  if (document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
    return waitFor(() => !!window.google?.maps?.Map);
  }

  // Load the script then wait for Map constructor
  return loadGoogleMapsScript(apiKey).then(() => waitFor(() => !!window.google?.maps?.Map));
}

/**
 * Preload the Google Maps script (call on hover/mount to reduce perceived latency).
 * Non-blocking — doesn't return a promise.
 */
export function preloadGoogleMapsScript(apiKey: string): void {
  if (!apiKey || window.google?.maps || document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = GOOGLE_MAPS_SCRIPT_ID;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
  script.async = true;
  document.head.appendChild(script);
}
